import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, ArchiveRestore, Presentation as PresentationIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ClientShell from "./ClientShell";
import { Progress } from "../../components/ui/progress";
import { usePresentationsList, useCreatePresentation, useUnarchivePresentation, useDeletePresentation } from "../../lib/presentationQueries";
import PromptDialog from "./presentation/PromptDialog";
import { SimpleDeleteConfirm } from "./shared/SimpleDeleteConfirm";

export default function PresentationList() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { data, isPending } = usePresentationsList(workspaceId, showArchived);
  const createPresentation = useCreatePresentation(workspaceId);
  const unarchivePresentation = useUnarchivePresentation(workspaceId);
  const deletePresentation = useDeletePresentation(workspaceId);
  const items = data?.items || [];

  async function handleConfirmDelete() {
    try {
      await deletePresentation.mutateAsync(deleteTarget.pres_id);
      toast.success(`"${deleteTarget.title}" was permanently deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't delete that presentation");
    }
  }

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="presentation-list-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-serif text-3xl text-ink">Presentations</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowArchived((s) => !s)} className="text-xs text-ink-soft hover:text-ink underline" data-testid="presentation-list-toggle-archived">
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
            <button
              onClick={() => setDialogOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 transition-colors"
              data-testid="presentation-list-create"
            >
              <Plus size={15} /> New presentation
            </button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="presentation-list-items">
          {isPending && <p className="text-ink-soft text-sm" data-testid="presentation-list-loading">Loading presentations…</p>}
          {items.map((p) => (
            <div key={p.pres_id} className="border border-ink/15 bg-white/50 rounded-lg p-5" data-testid={`presentation-list-item-${p.pres_id}`}>
              <div className="flex items-center gap-2 text-ink-soft mb-2">
                <PresentationIcon size={15} /><span className="text-xs uppercase tracking-wide">{p.status}</span>
              </div>
              <button
                onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presentation/${p.pres_id}`)}
                className="font-serif text-lg text-ink hover:text-terracotta text-left block mb-2"
                data-testid={`presentation-list-item-${p.pres_id}-open`}
              >
                {p.title}
              </button>
              <Progress value={p.completion_percent} className="h-1.5 mb-1" />
              <p className="text-xs text-ink-soft">{p.completion_percent}% completion</p>
              {p.status === "archived" && (
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={() => unarchivePresentation.mutate(p.pres_id)}
                    className="text-xs text-terracotta hover:text-terracotta/80 inline-flex items-center gap-1"
                    data-testid={`presentation-list-item-${p.pres_id}-unarchive`}
                  >
                    <ArchiveRestore size={12} /> Unarchive
                  </button>
                  <button
                    onClick={() => setDeleteTarget(p)}
                    className="text-xs text-ink-soft hover:text-red-600 inline-flex items-center gap-1"
                    data-testid={`presentation-list-item-${p.pres_id}-delete`}
                  >
                    <Trash2 size={12} /> Delete permanently
                  </button>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && !isPending && <p className="text-ink-soft text-sm" data-testid="presentation-list-empty">No presentations yet.</p>}
        </div>
      </div>
      <PromptDialog
        open={dialogOpen} onOpenChange={setDialogOpen} title="Name your presentation" placeholder="e.g. Q1 Board Update"
        confirmLabel="Create"
        onConfirm={(title) => createPresentation.mutate(title, {
          onSuccess: (res) => navigate(`/rehearsal-room/w/${workspaceId}/presentation/${res.project.pres_id}`),
        })}
        testId="presentation-list-create-dialog"
      />
      <SimpleDeleteConfirm
        open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this presentation permanently?"
        description={`"${deleteTarget?.title}" and all its idea cards, sections and version history will be permanently removed. This can't be undone.`}
        onConfirm={handleConfirmDelete} isPending={deletePresentation.isPending}
        testId="presentation-list-delete-confirm"
      />
    </ClientShell>
  );
}
